import { Marquee } from './Marquee';
import { Reveal } from './Reveal';
import { DOMINANCE, SAMPLE_ARCHES, UTILIZATION } from '../content';
import { CheckBadgeIcon, SearchIcon, TrendIcon } from './icons';

/**
 * The illustrative panels that sit above the copy in the top half of the page.
 *
 * All four are built from markup and SVG rather than screenshots. Three reasons
 * that matters here: they stay sharp at any width, they restyle with the rest
 * of the site instead of pinning it to whatever the app looked like the day
 * someone took a capture, and they carry real text into the prerendered
 * snapshot instead of an opaque image.
 *
 * The figures are illustrative — sample data demonstrating the mechanic, not a
 * report of live platform activity — and the surrounding sections say so.
 */

/** Shared dotted field behind the two data panels. */
function DottedField() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.35]"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 0)',
        backgroundSize: '22px 22px',
      }}
    />
  );
}

/**
 * Publishing an arche. Reads as a builder window mid-flow, which is the moment
 * the pillar beside it is about — the point where a contributor ships.
 */
export function PublishMock() {
  return (
    <div className="fias-device relative h-[300px] overflow-hidden p-6">
      <DottedField />

      <div className="relative flex h-full items-center justify-center">
        {/* Slides up into the frame, the way the panel it depicts appears when
            a contributor opens it. Further than a text reveal, and opaque. */}
        <Reveal className="fias-panel w-full max-w-sm p-5" distance={70} fade={false}>
          <div className="flex items-center justify-between">
            <span className="font-sans text-xs text-[color:var(--fias-muted)]">Arches</span>
            <span className="fias-chip !px-2 !py-0.5 !text-[0.625rem]">2</span>
          </div>

          <div className="mt-4 grid grid-cols-[1.2fr_1fr] gap-3">
            <div className="space-y-2">
              <div className="fias-mock-row h-7" />
              <div className="fias-mock-row h-7 w-3/4" />
            </div>
            {/* Asset slot — the image an arche ships with. */}
            <div className="fias-mock-row flex h-[68px] items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
                className="h-5 w-5 text-[color:var(--fias-dim)]"
              >
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <circle cx="8.5" cy="9.5" r="1.5" />
                <path d="m5 17 4.5-4.5 3 3L16 12l3 3" />
              </svg>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <span className="font-mono text-[0.625rem] text-[color:var(--fias-dim)]">
              11/11/2025
            </span>
            <span className="fias-chip !bg-white/90 !text-black">
              Publish
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="h-2.5 w-2.5"
              >
                <path d="M2 21 23 12 2 3v7l15 2-15 2Z" />
              </svg>
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/**
 * Finding an arche. The counterpart to publishing — the same catalogue seen
 * from the buyer's side.
 */
export function ArcheSearchMock() {
  return (
    <div className="fias-device relative h-[300px] overflow-hidden p-6">
      <DottedField />

      {/* The search field and each result slide up in turn, so the panel reads
          as a query resolving rather than a finished screenshot. */}
      <div className="relative">
        <Reveal className="flex items-center gap-2" distance={70} fade={false}>
          <div className="fias-mock-row flex flex-1 items-center gap-2 px-3 py-2.5">
            <span className="text-[color:var(--fias-dim)]">
              <SearchIcon />
            </span>
            <span className="font-sans text-xs text-[color:var(--fias-dim)]">Choose an Arche</span>
          </div>
          <span className="fias-chip !bg-white/90 !px-4 !py-2.5 !text-black">Search</span>
        </Reveal>

        <ul className="mt-3 space-y-2">
          {SAMPLE_ARCHES.map((name, index) => (
            <Reveal
              as="li"
              key={name}
              className="fias-mock-row flex items-center justify-between gap-3 px-3 py-2.5"
              distance={70}
              fade={false}
              delay={90 + index * 80}
              // Results recede down the list, so the panel reads as a window
              // onto a longer catalogue rather than a complete set of four.
              style={{ opacity: 1 - index * 0.18 }}
            >
              <span className="truncate font-sans text-xs text-[color:var(--fias-muted)]">
                {name}
              </span>
              <span className="h-3.5 w-3.5 shrink-0 text-[color:var(--fias-dim)]">
                <TrendIcon />
              </span>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Concentration of the current market. Two circles sized by share, with the
 * incumbents' names drifting underneath — the visual argument for the problem
 * stated beside it.
 */
export function DominanceMock() {
  return (
    <div className="fias-device relative h-[300px] overflow-hidden">
      <DottedField />

      <div className="relative flex h-full flex-col p-6">
        <Reveal className="flex justify-center">
          <span className="fias-chip">{DOMINANCE.caption}</span>
        </Reveal>

        <div className="flex flex-1 items-center justify-center">
          {/* Areas are proportional to share, so the 81/19 split is legible as
              size rather than only as a printed number. */}
          <Reveal className="relative flex items-center" delay={120} distance={32}>
            <div
              className="relative z-10 flex h-[74px] w-[74px] flex-col items-center justify-center rounded-full text-center"
              style={{
                background: 'radial-gradient(circle at 50% 25%, #2a2a2a, #0d0d0d)',
                boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.16)',
              }}
            >
              <span className="font-sans text-[0.5rem] text-[color:var(--fias-muted)]">others</span>
              <span className="font-sans text-sm font-semibold text-[color:var(--fias-bright)]">
                {DOMINANCE.others}%
              </span>
            </div>

            <div
              className="-ml-4 flex h-[146px] w-[146px] flex-col items-center justify-center rounded-full text-center"
              style={{
                background: 'radial-gradient(circle at 50% 20%, #333333, #0f0f0f)',
                boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.2)',
              }}
            >
              <span className="font-sans text-[0.625rem] text-[color:var(--fias-muted)]">
                Monoliths
              </span>
              <span className="font-sans text-lg font-semibold text-[color:var(--fias-bright)]">
                {DOMINANCE.monoliths}%
              </span>
            </div>
          </Reveal>
        </div>

        <Marquee durationSeconds={26} gapClassName="gap-7" className="-mx-6">
          {DOMINANCE.incumbents.map((name) => (
            <span
              key={name}
              className="font-sans text-xs font-medium text-[color:var(--fias-muted)]"
            >
              {name}
            </span>
          ))}
        </Marquee>
      </div>
    </div>
  );
}

/**
 * Earnings accruing to one entity. The answering panel: the same market, but
 * with the money reaching a specific participant.
 */
export function UtilizationMock() {
  return (
    <div className="fias-device relative h-[300px] overflow-hidden">
      <DottedField />

      <div className="relative flex h-full flex-col p-6">
        <Reveal className="flex items-center justify-between">
          <span className="fias-chip">{UTILIZATION.caption}</span>
          <span className="font-sans text-xs font-medium text-[color:var(--fias-muted)]">
            {UTILIZATION.window}
          </span>
        </Reveal>

        <Reveal className="relative flex-1" delay={120} distance={32}>
          <svg
            viewBox="0 0 320 110"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="fias-util-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
            </defs>
            <path
              d="M0 86 C 40 80, 70 72, 110 62 C 150 52, 165 58, 190 68 C 215 78, 235 62, 262 38 C 285 18, 302 12, 320 8 L 320 110 L 0 110 Z"
              fill="url(#fias-util-fill)"
            />
            <path
              d="M0 86 C 40 80, 70 72, 110 62 C 150 52, 165 58, 190 68 C 215 78, 235 62, 262 38 C 285 18, 302 12, 320 8"
              fill="none"
              stroke="rgba(255,255,255,0.55)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {/* Marker on the curve, with the figure it reads. Positioned in
              percentages so it tracks the SVG as the panel resizes. */}
          <div className="absolute left-[34%] top-[36%] -translate-x-1/2">
            <span className="font-sans text-xs font-medium text-[color:var(--fias-bright)]">
              {UTILIZATION.amount}
            </span>
          </div>
          <div
            className="absolute left-[34%] top-[57%] h-4 w-4 -translate-x-1/2 rounded-full border-2 border-white/80 bg-[#1a1a1a]"
            aria-hidden="true"
          />
        </Reveal>

        <Reveal
          className="fias-mock-row flex items-center justify-center gap-2 px-3 py-2.5"
          delay={240}
        >
          <span
            aria-hidden="true"
            className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[0.5rem] text-[color:var(--fias-muted)]"
          >
            ▲
          </span>
          <span className="font-sans text-xs font-medium text-[color:var(--fias-bright)]">
            {UTILIZATION.entity}
          </span>
          <span className="h-3.5 w-3.5 text-[color:var(--fias-muted)]">
            <CheckBadgeIcon />
          </span>
        </Reveal>
      </div>
    </div>
  );
}
